/**
 * Cellules — page CRUD complète des cellules / groupes de l'église.
 */
import { useState, useEffect, useCallback } from "react";
import { colors, fonts } from "../styles/tokens";
import { cellulesApi } from "../services/api";
import useMembers from "../hooks/useMembers";
import useToast from "../hooks/useToast";
import Card from "../components/Card";
import Badge from "../components/Badge";
import Button from "../components/Button";
import Avatar from "../components/Avatar";
import Select from "../components/Select";
import CelluleForm from "../components/CelluleForm";
import CelluleMembersModal from "../components/CelluleMembersModal";

export default function Cellules() {
  const { eglises } = useMembers();
  const toast = useToast();

  const [cellules, setCellules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEglise, setFilterEglise] = useState("all");

  // Modale création / édition
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // Modale gestion membres
  const [membersOpen, setMembersOpen] = useState(false);
  const [managing, setManaging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await cellulesApi.list();
      setCellules(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const filtered = filterEglise === "all"
    ? cellules
    : cellules.filter((c) => c.eglise_id === Number(filterEglise));

  // ─── Handlers ───
  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (cellule) => { setEditing(cellule); setFormOpen(true); };
  const openMembers = (cellule) => { setManaging(cellule); setMembersOpen(true); };

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        await cellulesApi.update(editing.id, payload);
        toast.success("Cellule mise à jour");
      } else {
        await cellulesApi.create(payload);
        toast.success(`Cellule « ${payload.nom} » créée`);
      }
      setFormOpen(false);
      setEditing(null);
      await load();
      return { ok: true };
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(msg);
      return { ok: false, error: msg };
    }
  };

  const handleDelete = async (cellule) => {
    try {
      await cellulesApi.remove(cellule.id);
      toast.success(`Cellule « ${cellule.nom} » supprimée`);
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de suppression");
    }
  };

  return (
    <div style={{ fontFamily: fonts.body, color: colors.text }}>
      {/* Header */}
      <header style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: "36px", fontWeight: 700, marginBottom: "4px" }}>
            Cellules
          </h1>
          <p style={{ color: colors.textMuted, fontSize: "14px" }}>
            {filtered.length} cellule{filtered.length > 1 ? "s" : ""} active{filtered.length > 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={openCreate}>+ Nouvelle cellule</Button>
      </header>

      {/* Filtre par église */}
      {eglises.length > 1 && (
        <Card style={{ marginBottom: "16px" }}>
          <div style={{ maxWidth: 320 }}>
            <Select
              label="Filtrer par église"
              value={filterEglise}
              onChange={setFilterEglise}
              options={[
                { value: "all", label: "Toutes les églises" },
                ...eglises.map((e) => ({ value: String(e.id), label: e.nom })),
              ]}
              placeholder={null}
            />
          </div>
        </Card>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Chargement des cellules...</div>
      ) : filtered.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "60px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>👥</div>
          <h3 style={{ fontFamily: fonts.heading, fontSize: "22px" }}>Aucune cellule</h3>
          <p style={{ color: colors.textMuted, marginTop: "8px" }}>
            Créez votre première cellule pour organiser vos membres en groupes.
          </p>
          <div style={{ marginTop: "16px" }}>
            <Button onClick={openCreate}>+ Nouvelle cellule</Button>
          </div>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: "16px" }}>
          {filtered.map((c) => (
            <CelluleCard
              key={c.id}
              cellule={c}
              onEdit={() => openEdit(c)}
              onMembers={() => openMembers(c)}
              onDelete={() => {
                if (window.confirm(`Supprimer la cellule « ${c.nom} » ?\nLes ${c.nb_membres} membre(s) seront détaché(s).`)) {
                  handleDelete(c);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Modales */}
      <CelluleForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        eglises={eglises}
        initial={editing}
      />

      <CelluleMembersModal
        open={membersOpen}
        onClose={() => { setMembersOpen(false); setManaging(null); }}
        cellule={managing}
        onChange={load}
      />
    </div>
  );
}

/** Carte d'une cellule. */
function CelluleCard({ cellule, onEdit, onMembers, onDelete }) {
  return (
    <Card hover>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {/* En-tête : nom + église */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
              <span style={{ fontSize: "18px" }}>🔵</span>
              <h3 style={{ fontFamily: fonts.heading, fontSize: "20px", fontWeight: 600 }}>
                {cellule.nom}
              </h3>
            </div>
            {cellule.description && (
              <p style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "4px" }}>
                {cellule.description}
              </p>
            )}
          </div>
          <Badge variant="gold" size="sm">{cellule.nb_membres}</Badge>
        </div>

        {/* Responsable + église */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {cellule.responsable_nom ? (
              <>
                <Avatar name={cellule.responsable_nom} size="xs" />
                <span style={{ color: colors.text }}>
                  Responsable&nbsp;:&nbsp;<strong style={{ fontWeight: 600 }}>{cellule.responsable_nom}</strong>
                </span>
              </>
            ) : (
              <span style={{ color: colors.textMuted, fontStyle: "italic" }}>
                Responsable : non assigné
              </span>
            )}
          </div>
          <div style={{ color: colors.textMuted }}>
            ⛪ {cellule.eglise_nom}
            {" · "}
            {cellule.nb_membres} membre{cellule.nb_membres > 1 ? "s" : ""}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "6px", paddingTop: "8px", borderTop: `1px solid ${colors.border}`, flexWrap: "wrap" }}>
          <Button size="sm" variant="ghost" onClick={onEdit}>Modifier</Button>
          <Button size="sm" variant="ghost" onClick={onMembers}>👥 Membres</Button>
          <Button size="sm" variant="danger" onClick={onDelete} style={{ marginLeft: "auto" }}>
            🗑
          </Button>
        </div>
      </div>
    </Card>
  );
}
